import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Undo2, CheckCircle2, Trophy, Pause, Play, Wrench, Plus } from "lucide-react";

/**
 * /scoring/:matchId — Referee point-by-point scorer.
 *
 * Season 3 mandate: referee taps only +POINT / UNDO / CORRECT / PAUSE / COMPLETE.
 * Score progression 0→15→30→40→GAME, DEUCE→AD→GAME, tiebreak 7-pts win-by-2
 * are all computed by the backend engine (tennis_engine.py). We just read state
 * and dispatch points.
 *
 * Backed by:
 *   GET  /matches/{id}/tennis/state
 *   POST /matches/{id}/tennis/point  {team:"a"|"b"}
 *   POST /matches/{id}/tennis/undo
 *   POST /matches/{id}/complete
 *   POST /matches/{id}/pause  (existing)
 *   POST /matches/{id}/resume (existing)
 *   PATCH /matches/{id}/set-score  (Correct Score — admin/ref for out-of-band fix)
 */
export default function SetBasedScoring() {
  const { matchId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [match, setMatch] = useState(null);
  const [tennis, setTennis] = useState(null); // { state, display, set_scores, totals, point_count }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showCorrect, setShowCorrect] = useState(false);
  const busyRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/matches/${matchId}`);
      setMatch(data);
      if (data?.format_type === "set_based") {
        try {
          const { data: t } = await api.get(`/matches/${matchId}/tennis/state`);
          setTennis(t);
        } catch { /* not initialised yet — fine */ }
      }
    } catch (e) { setErr(formatApiError(e)); }
  }, [matchId]);

  useEffect(() => { load(); }, [load]);

  // Route to the time-based scorer for legacy formats (kept for backward compat).
  useEffect(() => {
    if (match && match.format_type !== "set_based") {
      nav(`/umpire/${matchId}`, { replace: true });
    }
  }, [match, matchId, nav]);

  // Live sync via SSE — reload state on every point in this match.
  useEffect(() => {
    if (!matchId) return;
    const es = new EventSource(`${API_BASE}/public/events/stream`);
    const onMsg = (e) => {
      try {
        const d = JSON.parse(e.data);
        if (d.match_id === matchId || d.entity_id === matchId) load();
      } catch { /* silent */ }
    };
    es.addEventListener("match.point_scored", onMsg);
    es.addEventListener("match.set_scored", onMsg);
    es.addEventListener("match.finalized", onMsg);
    es.addEventListener("match.paused", onMsg);
    es.addEventListener("match.resumed", onMsg);
    return () => { try { es.close(); } catch { /* noop */ } };
  }, [matchId, load]);

  if (err) return <div className="p-10 text-red-400" data-testid="scoring-error">{err}</div>;
  if (!match) return <div className="p-10 text-zinc-500" data-testid="scoring-loading">Loading match…</div>;
  if (match.format_type !== "set_based") return <div className="p-10 text-zinc-500" data-testid="scoring-redirecting">Redirecting…</div>;

  const fc = match.format_config || {};
  const setsPer = fc.sets_per_match || 2;
  const gamesPerSet = fc.games_per_set || 6;
  const tiebreak = !!fc.tiebreak;
  const isReferee = user && (
    ["super_admin", "league_admin"].includes(user.role) ||
    (user.role === "court_umpire" && (!match.assigned_referee_id || match.assigned_referee_id === user.id))
  );
  const status = (match.status || "").toLowerCase();
  const finished = status === "finished";
  const paused = status === "paused";
  const canScore = isReferee && !finished;

  const disp = tennis?.display || { a: "0", b: "0", label: "" };
  const state = tennis?.state || {};
  const setScoresPersisted = (tennis?.set_scores || []).filter((s) => !s.in_progress);
  const totals = tennis?.totals || { total_games_won_a: 0, total_games_won_b: 0 };
  const setsWonA = state.sets_won_a || 0;
  const setsWonB = state.sets_won_b || 0;
  const curSetGamesA = state.current_set_games_a || 0;
  const curSetGamesB = state.current_set_games_b || 0;
  const inTiebreak = !!state.in_tiebreak;
  const isDeuce = !!state.deuce && !state.advantage;
  const isAdA = state.advantage === "a";
  const isAdB = state.advantage === "b";
  const gameDiff = (totals.total_games_won_a || 0) - (totals.total_games_won_b || 0);
  const pointCount = tennis?.point_count || 0;

  const scorePoint = async (team) => {
    if (!canScore || busyRef.current) return;
    busyRef.current = true; setBusy(true);
    try {
      const { data } = await api.post(`/matches/${matchId}/tennis/point`, { team });
      setTennis(data);
      // reload match for status/winner flip
      if (data?.state?.finished) await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { busyRef.current = false; setBusy(false); }
  };

  const undoPoint = async () => {
    if (!canScore || busyRef.current) return;
    if (pointCount === 0) return toast.error("Nothing to undo");
    busyRef.current = true; setBusy(true);
    try {
      const { data } = await api.post(`/matches/${matchId}/tennis/undo`);
      setTennis(data);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { busyRef.current = false; setBusy(false); }
  };

  const togglePause = async () => {
    if (!canScore) return;
    setBusy(true);
    try {
      const action = paused ? "resume" : "pause";
      await api.post(`/matches/${matchId}/${action}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const completeMatch = async () => {
    if (!window.confirm("Complete this match now? Final score becomes official.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/matches/${matchId}/complete`);
      setMatch(data);
      toast.success("Match completed");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const nameA = match.team_a?.name || match.team_a_name || "Team A";
  const nameB = match.team_b?.name || match.team_b_name || "Team B";
  const winnerName = match.winner_team_id === match.team_a_id ? nameA
    : match.winner_team_id === match.team_b_id ? nameB : "Draw";

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto" data-testid="scoring-set-based">
      <Button variant="ghost" onClick={() => nav(-1)} className="text-zinc-400 mb-4" data-testid="scoring-back">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back
      </Button>

      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">
            {match.stage} · {match.court} {match.seed ? `· Seed ${match.seed}` : ""}
          </div>
          <h1 className="font-anton text-4xl md:text-5xl uppercase tracking-tighter text-white mt-1 leading-none">
            {nameA} <span className="text-zinc-500">vs</span> {nameB}
          </h1>
          <div className="mt-2 text-xs text-zinc-500">
            Format: <b className="text-white/80">Best of {setsPer} sets · {gamesPerSet} games/set</b>{tiebreak ? " · tiebreak at 6-6" : ""} · point-by-point
          </div>
        </div>
        <StatusBadge status={match.status} />
      </div>

      {!isReferee && !finished && (
        <div className="mb-4 px-3 py-2 rounded border border-amber-500/40 bg-amber-500/10 text-amber-200 text-sm" data-testid="scoring-readonly-note">
          Read-only — you are not the assigned referee for this match.
        </div>
      )}

      {/* Live status banner (Deuce / AD / Tiebreak) */}
      {!finished && (
        <div className="mb-3 flex items-center justify-center min-h-[36px]">
          {inTiebreak && (
            <div className="px-3 py-1 rounded-full bg-[#F4B400] text-black text-[10px] font-bold uppercase tracking-widest" data-testid="scoring-tiebreak-badge">
              Tiebreak · First to 7 · Win by 2
            </div>
          )}
          {isDeuce && !inTiebreak && (
            <div className="px-3 py-1 rounded-full bg-[#e32424] text-white text-[10px] font-bold uppercase tracking-widest" data-testid="scoring-deuce-badge">Deuce</div>
          )}
          {(isAdA || isAdB) && !inTiebreak && (
            <div className="px-3 py-1 rounded-full bg-[#00A86B] text-black text-[10px] font-bold uppercase tracking-widest" data-testid="scoring-advantage-badge">
              Advantage {isAdA ? "A" : "B"}
            </div>
          )}
          {paused && (
            <div className="ml-2 px-3 py-1 rounded-full bg-white/10 text-white text-[10px] font-bold uppercase tracking-widest" data-testid="scoring-paused-badge">Paused</div>
          )}
        </div>
      )}

      {/* Big point-tap scoreboard */}
      {!finished && (
        <div className="grid grid-cols-2 gap-3 md:gap-4 mb-4" data-testid="scoring-tap-grid">
          <TapSide
            teamName={nameA}
            gameScore={disp.a}
            setsWon={setsWonA}
            currentGames={curSetGamesA}
            sets={setScoresPersisted.map((s) => s.a)}
            totalGames={totals.total_games_won_a || 0}
            disabled={!canScore || busy}
            highlight={isAdA}
            onScore={() => scorePoint("a")}
            testId="scoring-point-a"
          />
          <TapSide
            teamName={nameB}
            gameScore={disp.b}
            setsWon={setsWonB}
            currentGames={curSetGamesB}
            sets={setScoresPersisted.map((s) => s.b)}
            totalGames={totals.total_games_won_b || 0}
            disabled={!canScore || busy}
            highlight={isAdB}
            onScore={() => scorePoint("b")}
            testId="scoring-point-b"
          />
        </div>
      )}

      {/* Control row */}
      {isReferee && !finished && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="scoring-controls">
          <ControlBtn onClick={undoPoint} disabled={busy || pointCount === 0} icon={Undo2} label="Undo Last Point" testId="scoring-undo" />
          <ControlBtn onClick={() => setShowCorrect(true)} disabled={busy} icon={Wrench} label="Correct Score" testId="scoring-correct-open" />
          <ControlBtn onClick={togglePause} disabled={busy} icon={paused ? Play : Pause} label={paused ? "Resume" : "Pause"} testId="scoring-pause" />
          <ControlBtn onClick={completeMatch} disabled={busy} icon={CheckCircle2} label="Complete Match" tone="ok" testId="scoring-complete" />
        </div>
      )}

      {/* Live totals + set history */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="scoring-totals">
        <Totals label="Sets Won A" value={setsWonA} tone="ok" testid="totals-sets-a" />
        <Totals label="Sets Won B" value={setsWonB} tone="ok" testid="totals-sets-b" />
        <Totals label="Total Games (A – B)" value={`${totals.total_games_won_a || 0} – ${totals.total_games_won_b || 0}`} testid="totals-games" />
        <Totals
          label="Game Difference (A)"
          value={`${gameDiff > 0 ? "+" : ""}${gameDiff}`}
          tone={gameDiff > 0 ? "ok" : gameDiff < 0 ? "warn" : "neutral"}
          testid="totals-gd"
        />
      </div>

      {/* Persisted set history strip */}
      {setScoresPersisted.length > 0 && (
        <Card className="p-3 bg-[#0f1a15] border-white/10 mb-4" data-testid="scoring-set-history">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Completed sets</div>
          <div className="flex flex-wrap gap-2">
            {setScoresPersisted.map((s, i) => (
              <div key={i} className="px-3 py-1.5 rounded border border-white/10 bg-black/40 text-sm font-anton tabular-nums" data-testid={`scoring-set-history-${i + 1}`}>
                <span className="text-zinc-500 text-[10px] uppercase tracking-widest mr-2">Set {i + 1}</span>
                {s.a}-{s.b}
                {s.tiebreak_a != null && (
                  <span className="text-zinc-500 text-[10px] ml-2">tb {s.tiebreak_a}-{s.tiebreak_b}</span>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {finished && (
        <div className="mt-6 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-3" data-testid="scoring-final">
          <Trophy className="w-6 h-6 text-emerald-300" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-300">Final</div>
            <div className="font-anton text-2xl text-white">
              {winnerName} wins
              {" · "}
              <span className="tabular-nums">{setScoresPersisted.map((s) => `${s.a}-${s.b}`).join(", ")}</span>
            </div>
          </div>
        </div>
      )}

      {/* Correct Score dialog — out-of-band fix for admins */}
      {showCorrect && (
        <CorrectScoreDialog
          matchId={matchId}
          setsPer={setsPer}
          setScores={setScoresPersisted}
          onClose={() => setShowCorrect(false)}
          onDone={async () => { setShowCorrect(false); await load(); }}
        />
      )}
    </div>
  );
}

function TapSide({ teamName, gameScore, setsWon, currentGames, sets, totalGames, disabled, highlight, onScore, testId }) {
  return (
    <button
      onClick={onScore}
      disabled={disabled}
      data-testid={testId}
      className={`p-4 md:p-6 rounded-2xl border transition-all min-h-[280px] flex flex-col justify-between text-left ${
        disabled ? "border-white/10 bg-white/5 cursor-not-allowed opacity-60"
                 : highlight
                 ? "border-[#00A86B] bg-[#00A86B]/10 hover:bg-[#00A86B]/15 active:scale-95"
                 : "border-[#F4B400]/30 bg-[#F4B400]/5 hover:border-[#F4B400] hover:bg-[#F4B400]/10 active:scale-95"
      }`}
    >
      <div>
        <div className="text-sm md:text-base font-bold uppercase tracking-widest text-zinc-300 truncate">{teamName}</div>
        <div className="flex items-center gap-3 mt-1">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Sets</div>
          <div className="font-anton text-xl tabular-nums text-emerald-400" data-testid={`${testId}-sets`}>{setsWon}</div>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {sets.map((n, i) => (
            <div key={i} className="text-[10px] uppercase tracking-widest bg-black/40 border border-white/10 rounded px-1.5 py-0.5">
              <span className="text-zinc-500">S{i + 1}</span>{" "}<span className="font-anton tabular-nums">{n}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Current set · games {currentGames}</div>
        <div className="font-anton text-[100px] md:text-[140px] leading-none tabular-nums text-[#F4B400]" data-testid={`${testId}-game`}>
          {gameScore}
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Tap to score +1</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total <b className="text-white">{totalGames}</b></div>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-center gap-1 text-[11px] uppercase tracking-widest text-[#F4B400] font-bold">
        <Plus className="w-3.5 h-3.5" /> Point
      </div>
    </button>
  );
}

function ControlBtn({ onClick, disabled, icon: Icon, label, tone, testId }) {
  const toneCls = tone === "ok"
    ? "bg-emerald-500 hover:bg-emerald-400 text-black"
    : "border border-white/10 bg-black/40 hover:border-[#F4B400]/40 text-white";
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testId}
      className={`h-16 rounded-xl flex flex-col items-center justify-center gap-1 transition ${
        disabled ? "opacity-40 cursor-not-allowed border border-white/5 bg-white/5" : toneCls
      }`}>
      <Icon className="w-5 h-5" />
      <span className="text-[10px] uppercase tracking-widest font-bold">{label}</span>
    </button>
  );
}

function CorrectScoreDialog({ matchId, setsPer, setScores, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(
    Array.from({ length: setsPer }, (_, i) => ({
      a: setScores[i]?.a ?? 0,
      b: setScores[i]?.b ?? 0,
    }))
  );

  const save = async () => {
    if (!window.confirm("Correcting scores overrides the live point log for those sets. Continue?")) return;
    setBusy(true);
    try {
      for (let i = 0; i < rows.length; i++) {
        await api.patch(`/matches/${matchId}/set-score`, {
          set_index: i + 1,
          a: Number(rows[i].a || 0),
          b: Number(rows[i].b || 0),
        });
      }
      toast.success("Score corrected");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="max-w-md w-full p-5 bg-[#0f1a15] border-[#F4B400]/40" onClick={(e) => e.stopPropagation()} data-testid="scoring-correct-dialog">
        <div className="font-anton text-xl uppercase text-white mb-1">Correct Score</div>
        <div className="text-xs text-zinc-500 mb-4">
          Use this for out-of-band fixes only. Point-by-point Undo is always preferred. All corrections are audit-logged.
        </div>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-3 gap-2 items-center" data-testid={`scoring-correct-row-${i + 1}`}>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Set {i + 1}</div>
              <input type="number" min={0} max={20} value={r.a}
                onChange={(e) => setRows(rows.map((x, idx) => idx === i ? { ...x, a: e.target.value } : x))}
                className="bg-black/40 border border-white/10 rounded px-2 py-2 text-white text-center font-anton text-lg tabular-nums"
                data-testid={`scoring-correct-${i + 1}-a`} />
              <input type="number" min={0} max={20} value={r.b}
                onChange={(e) => setRows(rows.map((x, idx) => idx === i ? { ...x, b: e.target.value } : x))}
                className="bg-black/40 border border-white/10 rounded px-2 py-2 text-white text-center font-anton text-lg tabular-nums"
                data-testid={`scoring-correct-${i + 1}-b`} />
            </div>
          ))}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="text-xs px-3 py-2 rounded border border-white/10 text-zinc-300" data-testid="scoring-correct-cancel">Cancel</button>
          <Button onClick={save} disabled={busy} className="bg-[#F4B400] text-black font-bold uppercase tracking-widest" data-testid="scoring-correct-save">
            {busy ? "Saving…" : "Apply Correction"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Totals({ label, value, tone = "neutral", testid }) {
  const c = tone === "ok" ? "text-emerald-300" : tone === "warn" ? "text-red-300" : "text-white";
  return (
    <Card className="p-3 bg-[#0f1a15] border-white/10">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-anton text-3xl tabular-nums leading-none mt-1 ${c}`} data-testid={testid}>{value ?? 0}</div>
    </Card>
  );
}

function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  const map = {
    live: { cls: "bg-red-500/20 text-red-300 border-red-500/40", label: "Live" },
    finished: { cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", label: "Finished" },
    scheduled: { cls: "bg-white/10 text-zinc-300 border-white/20", label: "Not Started" },
    paused: { cls: "bg-amber-500/20 text-amber-300 border-amber-500/40", label: "Paused" },
  };
  const v = map[s] || map.scheduled;
  return <Badge className={`border ${v.cls}`} data-testid="scoring-status">{v.label}</Badge>;
}
